import Vue from 'vue';
import VueApollo from 'vue-apollo';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createApolloClient } from 'vue-cli-plugin-apollo/graphql-client';
// eslint-disable-next-line import/no-extraneous-dependencies
import { setContext } from 'apollo-link-context';
// eslint-disable-next-line import/no-extraneous-dependencies
import { onError } from 'apollo-link-error';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ApolloLink, fromPromise } from 'apollo-link';
import { lsStorageService, lsStorageServiceConstants } from 'ls-vue-common';
// eslint-disable-next-line import/no-cycle
import resolvers from './graphql/resolvers';
import typeDefs from './graphql/typeDefs';

// Install the vue plugin
Vue.use(VueApollo);

// Http endpoint
const httpEndpoint = process.env.VUE_APP_GRAPH_BASE_URL;
// const httpEndpoint = 'http://localhost:59437/'; // uncomment when developing locally. grab port from cli output (near rocketship emoji)

const lighthouseHttpEndpoint = `${process.env.VUE_APP_API_BASE_URL}/graphql`;

const buildHeaders = (headers) => {
    const authtoken = lsStorageService.get(lsStorageServiceConstants.AUTH_TOKEN);
    return {
        ...headers,
        'x-auth-token': authtoken,
        'x-ls-client': process.env.VUE_APP_NAME,
        'apollographql-client-name': process.env.VUE_APP_NAME,
        'apollographql-client-version': process.env.VUE_APP_VERSION,
    };
};

const link = setContext((_, { headers }) => {
    const next = buildHeaders(headers);
    // Devbox-only: tell shared dev-graph which per-branch api to call.
    // Paired with PE-75032 on graph.lawnstarter.com.
    if (process.env.VUE_APP_ENVIRONMENT === 'devbox') {
        next['x-api-base-url'] = process.env.VUE_APP_API_BASE_URL;
    }
    return { headers: next };
});

const MAIN_API_JWT_STORAGE_KEY = 'jwt.mainApiToken';

const getMainApiJwt = () => lsStorageService.get(MAIN_API_JWT_STORAGE_KEY);

let mintMainApiJwtPromise = null;

const mintMainApiJwt = () => {
    if (mintMainApiJwtPromise) {
        return mintMainApiJwtPromise;
    }

    // `component` is deprecated but remains the supported user-context path (matches admin).
    const query = `
        query GetJWT($component: JwtComponentEnum) {
            getJWT(input: { component: $component }) {
                token
            }
        }
    `;

    mintMainApiJwtPromise = fetch(lighthouseHttpEndpoint, {
        method: 'POST',
        headers: buildHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ query, variables: { component: 'LS_API' } }),
    })
        .then((response) => response.json())
        .then((body) => {
            const token = body && body.data && body.data.getJWT && body.data.getJWT.token;
            if (!token) {
                throw new Error('Unable to mint LS_API JWT for the Lighthouse client');
            }
            lsStorageService.set(MAIN_API_JWT_STORAGE_KEY, token);
            return token;
        })
        .finally(() => {
            mintMainApiJwtPromise = null;
        });

    return mintMainApiJwtPromise;
};

const lighthouseAuthLink = setContext((_, { headers }) => {
    const next = buildHeaders(headers);
    const jwt = getMainApiJwt();
    if (jwt) {
        next.authorization = `Bearer ${jwt}`;
    }
    return { headers: next };
});

const lighthouseErrorLink = onError(({ graphQLErrors, operation, forward }) => {
    if (!graphQLErrors) {
        return undefined;
    }

    const isUnauthenticated = graphQLErrors.some((error) =>
        /unauthenticated/i.test((error && error.message) || '')
    );

    if (!isUnauthenticated || operation.getContext().jwtRetried) {
        return undefined;
    }

    return fromPromise(mintMainApiJwt().catch(() => null)).flatMap(() => {
        operation.setContext({ jwtRetried: true });
        return forward(operation);
    });
});

const lighthouseLink = ApolloLink.from([lighthouseErrorLink, lighthouseAuthLink]);

// Create apollo clients
export const { apolloClient } = createApolloClient({
    typeDefs,
    resolvers,
    httpEndpoint,
    link,
});

export const { apolloClient: lighthouseClient } = createApolloClient({
    httpEndpoint: lighthouseHttpEndpoint,
    link: lighthouseLink,
});

// Call this in the Vue app file
export function createProvider() {
    // Create vue apollo provider
    const apolloProvider = new VueApollo({
        clients: {
            default: apolloClient,
            lighthouse: lighthouseClient,
        },
        defaultClient: apolloClient,
        errorHandler(error) {
            console.log(
                '%cError',
                'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;',
                error.message
            );
        },
    });

    return apolloProvider;
}
